import React, { useState } from "react";
import { Check, Star, Mail, BookOpen, ChevronDown, ChevronUp } from "lucide-react";
import { seriesData } from "../data/series";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const allSeries = Object.values(seriesData);
const freeSeries = allSeries.filter(s => s.tier === "free");
const patronSeries = allSeries.filter(s => s.tier === "patron");

const SubscribePage = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [selectedTier, setSelectedTier] = useState("free");
  const [selectedSeries, setSelectedSeries] = useState(
    freeSeries.map(s => s.slug)
  );
  const [showSeriesPicker, setShowSeriesPicker] = useState(false);

  const toggleSeries = (slug) => {
    setSelectedSeries(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  };

  const handleFreeSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await axios.post(`${BACKEND_URL}/api/subscribe`, {
        email,
        series: selectedSeries.length > 0 ? selectedSeries : undefined,
      });
      setSubmitStatus({ type: "success", message: "Welcome to Freddy Fiction! Check your inbox for confirmation." });
      setEmail("");
    } catch {
      setSubmitStatus({ type: "error", message: "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaidSubscribe = async (tier) => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${BACKEND_URL}/api/checkout/create`, {
        originUrl,
        email: email || undefined,
        tier,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to start checkout. Please try again.";
      setSubmitStatus({ type: "error", message: msg });
      setIsSubmitting(false);
    }
  };

  const tiers = [
    {
      id: "free",
      name: "Free Reader",
      price: "$0",
      period: "forever",
      description: "Read all three complete stories on the website.",
      benefits: [
        "Full access to The Lattice, SOMNIUM, and The Silver Star",
        "All chapters available on the website",
        "Mobile-optimized reading experience",
      ],
      accent: null,
    },
    {
      id: "reader",
      name: "Reader",
      price: "$5",
      period: "month",
      description: "Everything free, plus weekly chapter drops to your inbox.",
      benefits: [
        "Everything in Free",
        "Weekly chapter emails delivered to your inbox",
        "Choose which series you receive",
        "Never miss a new chapter",
      ],
      accent: "#FF4343",
      popular: true,
    },
    {
      id: "patron",
      name: "Patron",
      price: "$9.99",
      period: "month",
      description: "Full access — read, listen, and download every story.",
      benefits: [
        "Everything in Reader",
        "Instant access to patron-exclusive stories",
        "Audio versions of every chapter — listen on your phone, even offline",
        "Download chapters & full series as PDFs",
        "Comment and discuss with other patrons",
        "RSS feed to follow in any podcast or reader app",
        "Support independent fiction",
      ],
      accent: "#d4a574",
    },
  ];

  // Determine which series are selectable based on tier
  const selectableSeries = selectedTier === "patron" ? allSeries : freeSeries;

  return (
    <div data-testid="subscribe-page" className="min-h-screen bg-ff-bg pt-24 md:pt-32">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 text-center mb-16">
        <p className="label text-ff-accent mb-4">JOIN FREDDY FICTION</p>
        <h1 className="font-serif text-display text-ff-text mb-6">Choose how you want to read.</h1>
        <p className="text-ff-text-muted font-light text-lg max-w-2xl mx-auto">
          All three published series are completely free to read on the website.
          Upgrade for weekly email delivery or early access to new releases.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 mb-16">
        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const isSelected = selectedTier === tier.id;
            return (
              <div
                key={tier.id}
                data-testid={`tier-card-${tier.id}`}
                className={`relative p-6 md:p-8 border transition-all cursor-pointer ${
                  isSelected
                    ? "border-ff-accent bg-ff-surface"
                    : "border-ff-border bg-ff-bg hover:border-ff-text-muted"
                }`}
                onClick={() => setSelectedTier(tier.id)}
                style={tier.popular ? { borderTopWidth: "3px", borderTopColor: tier.accent } : {}}
              >
                {tier.popular && (
                  <div
                    className="absolute -top-3 left-6 px-3 py-1 text-ff-text text-xs font-semibold tracking-wider"
                    style={{ background: tier.accent }}
                  >
                    MOST POPULAR
                  </div>
                )}

                {isSelected && !tier.popular && (
                  <div className="absolute -top-3 left-6 px-3 py-1 bg-ff-accent text-ff-text text-xs font-semibold tracking-wider">
                    SELECTED
                  </div>
                )}

                <div className="flex items-center gap-2 mb-2">
                  <h2 className="font-serif text-xl text-ff-text">{tier.name}</h2>
                  {tier.id === "patron" && <Star size={16} className="text-amber-500" fill="currentColor" />}
                  {tier.id === "reader" && <Mail size={16} className="text-ff-accent" />}
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-serif text-ff-text">{tier.price}</span>
                  <span className="text-ff-text-muted text-sm"> / {tier.period}</span>
                </div>

                <p className="text-ff-text-muted text-sm mb-6">{tier.description}</p>

                <ul className="space-y-2.5 mb-6">
                  {tier.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <Check size={16} className="shrink-0 mt-0.5" style={{ color: tier.accent || "#FF4343" }} />
                      <span className="text-ff-text-muted">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Series Picker */}
      {(selectedTier === "free" || selectedTier === "reader") && (
        <div className="max-w-md mx-auto px-4 md:px-6 mb-8">
          <button
            onClick={() => setShowSeriesPicker(!showSeriesPicker)}
            data-testid="series-picker-toggle"
            className="w-full flex items-center justify-between p-4 bg-ff-surface border border-ff-border text-ff-text text-sm hover:border-ff-text-muted transition-colors"
          >
            <span className="flex items-center gap-2">
              <BookOpen size={16} className="text-ff-accent" />
              Choose your series ({selectedSeries.length} selected)
            </span>
            {showSeriesPicker ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showSeriesPicker && (
            <div className="border border-ff-border border-t-0 bg-ff-surface divide-y divide-ff-border" data-testid="series-picker-list">
              {selectableSeries.map((s) => {
                const checked = selectedSeries.includes(s.slug);
                return (
                  <label
                    key={s.slug}
                    data-testid={`series-pick-${s.slug}`}
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-ff-bg transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSeries(s.slug)}
                      className="accent-ff-accent w-4 h-4"
                    />
                    <div
                      className="w-2 h-8 rounded-full shrink-0"
                      style={{ background: s.accentColor }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-ff-text text-sm font-medium">{s.title}</p>
                      <p className="text-ff-text-muted text-xs truncate">{s.logline}</p>
                    </div>
                    {s.tier === "patron" && (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#c8860a22', color: '#c8860a', border: '1px solid #c8860a44' }}>
                        Patron
                      </span>
                    )}
                  </label>
                );
              })}

              {selectedTier !== "patron" && patronSeries.length > 0 && (
                <div className="p-4 text-center">
                  <p className="text-ff-text-muted text-xs">
                    {patronSeries.length} more series available with{" "}
                    <button
                      onClick={(e) => { e.preventDefault(); setSelectedTier("patron"); setShowSeriesPicker(false); }}
                      className="text-ff-accent underline hover:text-ff-text transition-colors"
                      data-testid="upgrade-to-patron-link"
                    >
                      Patron
                    </button>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Form */}
      <div className="max-w-md mx-auto px-4 md:px-6 pb-24">
        <div className="p-8 bg-ff-surface border border-ff-border">
          <h3 className="font-serif text-xl text-ff-text mb-6 text-center">
            {selectedTier === "free" ? "Subscribe Free" : selectedTier === "reader" ? "Become a Reader" : "Become a Patron"}
          </h3>

          {selectedTier === "free" ? (
            <form onSubmit={handleFreeSubscribe} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                data-testid="subscribe-email"
                className="input-dark w-full"
                required
              />
              <button
                type="submit"
                data-testid="subscribe-free-btn"
                disabled={isSubmitting}
                className="btn-primary w-full disabled:opacity-50"
              >
                {isSubmitting ? "Subscribing..." : "Subscribe Free"}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email (optional)"
                data-testid="subscribe-email-paid"
                className="input-dark w-full"
              />
              <button
                onClick={() => handlePaidSubscribe(selectedTier)}
                data-testid="subscribe-paid-btn"
                disabled={isSubmitting}
                className="btn-primary w-full disabled:opacity-50"
              >
                {isSubmitting ? "Loading..." : `Continue to Payment — ${selectedTier === "reader" ? "$5/mo" : "$9.99/mo"}`}
              </button>
              <p className="text-ff-text-muted text-xs text-center">
                Secure checkout powered by Stripe. Cancel anytime.
              </p>
            </div>
          )}

          {submitStatus && (
            <p className={`mt-4 text-sm text-center ${submitStatus.type === "success" ? "text-green-400" : "text-red-400"}`}>
              {submitStatus.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
